/**
 * User Detail Component
 * 
 * Displays comprehensive user profile with:
 * - Header with avatar, status, and actions (Edit, Resend Invitation)
 * - Details card (contact, username, products, invitation, dates)
 * - Memberships (user → organization) section — this is where organizations live
 */

import * as React from 'react'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useApiQuery, useApiMutation } from '@/api/ApiQueryServiceProvider'
import { useToast } from '@/contexts/ToastContext'
import {
  ArrowLeft,
  Mail,
  Loader2,
  AlertCircle,
  User as UserIcon,
  Edit,
} from 'lucide-react'
import { ProfileSection } from '@/components/sensnet/ProfileLayout'
import { UserAvatar } from './UserAvatar'
import { InvitationStatusBadge } from './InvitationStatusBadge'
import { MembershipList } from './MembershipList'
import {
  UserStatus,
  USER_STATUS_VARIANTS,
  type Invitation,
  type User,
} from '@/types/users'
import { findUserInvitation, invitationDisplayStatus, isInvitationActionable } from '@/lib/invitations'

// ============================================================================
// Component Props
// ============================================================================

export interface UserDetailProps {
  userNumber: number
  onBack?: () => void
  onNavigateToEdit?: () => void
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format timestamp to readable date
 */
function formatDate(timestamp?: number): string {
  if (!timestamp) return 'N/A'
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Solid status colours that read on the dark header.
const HEADER_STATUS_CLASSES: Record<string, string> = {
  green: 'bg-emerald-500 text-white hover:bg-emerald-500',
  red: 'bg-red-500 text-white hover:bg-red-500',
  gray: 'bg-slate-500 text-white hover:bg-slate-500',
}

// ============================================================================
// Component Implementation
// ============================================================================

export function UserDetail(props: UserDetailProps) {
  const { userNumber, onBack } = props
  const { toast } = useToast()

  // Fetch user detail from API
  const { data, isLoading, isError, refetch } = useApiQuery('getUserByNumber', { userNumber }, {})

  // Invitation status/number aren't on the user record — match via the invitation list.
  const { data: invitationData } = useApiQuery('getInvitationList', {}, {})
  const invitations: Invitation[] = (invitationData as any)?.content?.invitations ?? []

  // Resend invitation mutation
  const resendInvitation = useApiMutation('resendInvitation', 'POST', {
    invalidates: ['getInvitationList'],
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Invitation has been resent successfully.',
        type: 'success',
      })
      refetch()
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to resend invitation: ${error.message}`,
        type: 'error',
      })
    },
  })

  // Map API response to user
  // Backend wraps response as { content: { user } }; organizations are objects not primitives
  const rawUser = data?.content?.user
  const userStatusValues = Object.values(UserStatus) as string[]
  const normalizeUserStatus = (raw?: string): UserStatus => {
    const lower = String(raw ?? '').toLowerCase()
    return userStatusValues.includes(lower) ? (lower as UserStatus) : UserStatus.Active
  }

  const invitation = rawUser ? findUserInvitation(invitations, rawUser) : undefined

  const user: User | null = rawUser ? {
    userNumber: rawUser.userNumber,
    firstName: rawUser.firstName,
    lastName: rawUser.lastName,
    emailAddress: rawUser.emailAddress ?? undefined,
    mobileNumber: rawUser.mobileNumber ?? undefined,
    countryCode: rawUser.countryCode ?? undefined,
    profilePicture: rawUser.profilePicture ?? undefined,
    userStatus: normalizeUserStatus(rawUser.userStatus ?? undefined),
    invitationStatus: invitation ? invitationDisplayStatus(invitation) : undefined,
    createdAt: rawUser.createdAt ?? undefined,
    updatedAt: rawUser.updatedAt ?? undefined,
  } : null

  // Handlers
  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      window.history.back()
    }
  }

  const handleResendInvitation = () => {
    if (!invitation) return
    resendInvitation.mutate({ invitationNumber: invitation.invitationNumber })
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Error state
  if (isError || !user) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <p className="text-destructive">Failed to load user details</p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    )
  }

  const statusVariant = USER_STATUS_VARIANTS[user.userStatus] ?? { label: user.userStatus ?? 'Unknown', variant: 'gray' as const }
  // Resend is allowed only while the backend still has it `pending` (incl. lazily-expired).
  const showResendInvitation = !!invitation && isInvitationActionable(invitation)
  const phone = user.mobileNumber
    ? [user.countryCode && (user.countryCode.startsWith('+') ? user.countryCode : `+${user.countryCode}`), user.mobileNumber].filter(Boolean).join(' ')
    : undefined
  const tenants: string[] = rawUser?.tenants ?? []

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={handleBack} className="gap-2 -ml-2">
        <ArrowLeft className="h-4 w-4" />
        Back
      </Button>

      {/* Header: identity + status + actions */}
      <div className="rounded-lg bg-slate-700 px-6 py-5 text-white shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <UserAvatar user={user} size="lg" className="ring-2 ring-white/20" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-xl font-bold">{user.firstName} {user.lastName}</h1>
                <Badge className={HEADER_STATUS_CLASSES[statusVariant.variant] ?? HEADER_STATUS_CLASSES.gray}>
                  {statusVariant.label}
                </Badge>
              </div>
              {user.emailAddress && (
                <a
                  href={`mailto:${user.emailAddress}`}
                  className="mt-1 flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"
                >
                  <Mail className="h-4 w-4 shrink-0" />
                  <span className="truncate">{user.emailAddress}</span>
                </a>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {showResendInvitation && (
              <Button variant="secondary" onClick={handleResendInvitation} disabled={resendInvitation.isPending}>
                <Mail className="h-4 w-4 mr-2" />
                {resendInvitation.isPending ? 'Sending...' : 'Resend Invitation'}
              </Button>
            )}
            <Button asChild>
              <Link to="/users/$userNumber/edit" params={{ userNumber: String(userNumber) }}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Details */}
      <ProfileSection title="Details" icon={<UserIcon className="h-4 w-4" />} variant="dark">
        <dl className="grid grid-cols-1 gap-x-8 gap-y-6 p-5 sm:grid-cols-2 lg:grid-cols-4">
          <DetailItem label="Email" value={user.emailAddress} />
          <DetailItem label="Phone" value={phone} />
          <DetailItem label="Username" value={rawUser?.userName} />
          <DetailItem label="User number" value={<span className="font-mono">{user.userNumber}</span>} />
          <DetailItem
            label="Products"
            value={tenants.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {tenants.map((t) => (
                  <Badge key={t} variant="outline" className="capitalize">{t}</Badge>
                ))}
              </div>
            )}
          />
          <DetailItem label="Invitation" value={<InvitationStatusBadge status={user.invitationStatus} />} />
          <DetailItem label="Created" value={user.createdAt && formatDate(user.createdAt)} />
          <DetailItem label="Last updated" value={user.updatedAt && formatDate(user.updatedAt)} />
        </dl>
      </ProfileSection>

      {/* Memberships (user -> organization -> role) */}
      <MembershipList
        userNumber={userNumber}
        disabled={user.userStatus === UserStatus.Archived}
      />
    </div>
  )
}

/** Label-over-value cell for the Details grid; empty values render as a muted dash. */
function DetailItem({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-1.5 break-words text-sm font-medium text-foreground">
        {value || <span className="font-normal text-muted-foreground">—</span>}
      </dd>
    </div>
  )
}
